import { TplMgr } from "@/wab/shared/TplMgr";
import { ensure, mkShortId } from "@/wab/shared/common";
import { allComponentVariants } from "@/wab/shared/core/components";
import { Pt } from "@/wab/shared/geom";
import { Arena, ArenaFrame, Site } from "@/wab/shared/model/classes";
import { mmToCssPx } from "./book";
import {
  ResolvedPublishingPage,
  resolvePublishingComposition,
} from "./composition";
import {
  PUBLISHING_VIEW_KEY,
  PublishingView,
  migrateLegacyBook,
  publishingViewSchema,
  readPublishingDocument,
  writePublishingDocument,
} from "./document";

export function readPublishingView(
  site: Pick<Site, "flags">,
): PublishingView | undefined {
  const raw = site.flags[PUBLISHING_VIEW_KEY];
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw !== "string")
    throw new Error("Invalid publishing view identity");
  return publishingViewSchema.parse(JSON.parse(raw));
}

export function isPublishingArena(site: Site, arena: Arena) {
  const view = readPublishingView(site);
  return !!view && arena.publishingViewId === view.id;
}

export type PublishingPrintLayout = "spread" | "column";

/** Per-row spacing hook for editor chrome: called once per row (after it) with the
 * page ids of the row that follows (empty after the last row). */
export type RowGap = (row: {
  rowIndex: number;
  nextRowPageIds: readonly string[];
}) => number;

/** Display-only placement in CSS px, never a binding gutter or print geometry.
 * Switching layouts only projects existing adapters; it never changes the document or frames.
 */
export function getPublishingViewLayout(
  site: Site,
  arena: Arena,
  mode: PublishingPrintLayout = "spread",
  rowGap: number | RowGap = 64,
  topInset: number | ((firstRowPageIds: readonly string[]) => number) = 0,
) {
  const doc = ensure(
    readPublishingDocument(site),
    "Publishing document not initialized",
  );
  const view = ensure(
    readPublishingView(site),
    "Publishing view not initialized",
  );
  if (arena.publishingViewId !== view.id)
    throw new Error("Not the managed publishing view");
  const composition = resolvePublishingComposition(site, doc);
  const rows = mode === "column"
    ? composition.pages.map((page) => [page])
    : composition.spreads.map((spread) => [spread.left, spread.right]);
  // Facing pages meet at their TRIM edges. Media boxes (bleed + slug) may overlap
  // at the spine and differ per page, so the shared spine x is the furthest any
  // row needs on its left; media origins then follow from each page's own trim
  // offset and never go negative. Display only: canonical mm geometry is untouched.
  const spineX = Math.max(
    0,
    ...composition.spreads.flatMap((s) => [
      s.left
        ? mmToCssPx(s.left.boxes.trim.x + s.left.boxes.trim.width)
        : 0,
      s.right ? mmToCssPx(s.right.boxes.trim.x) : 0,
    ]),
  );
  const pageIds = (row: (typeof rows)[number]) =>
    row.flatMap((page) => (page ? [page.id] : []));
  const gapAfter = (rowIndex: number) =>
    typeof rowGap === "number"
      ? rowGap
      : rowGap({
          rowIndex,
          nextRowPageIds: rows[rowIndex + 1] ? pageIds(rows[rowIndex + 1]) : [],
        });
  let y = typeof topInset === "number" ? topInset : topInset(pageIds(rows[0] ?? []));
  let lastGap = 0;
  const pages = rows.flatMap((rowPages, rowIndex) => {
    const rowTrimTop = Math.max(
      0,
      ...rowPages.map((p) => (p ? mmToCssPx(p.boxes.trim.y) : 0)),
    );
    const row = rowPages.flatMap((page) => {
      if (!page) return [];
      const frame = page.component
        ? ensure(
            arena.children.find((f) => f.uuid === view.frames[page.id]),
            `Page ${page.id} has no publishing editing frame`,
          )
        : undefined;
      const mediaX =
        mode === "column"
          ? 0
          : page.side === "left"
            ? spineX -
              mmToCssPx(page.boxes.trim.x + page.boxes.trim.width)
            : spineX - mmToCssPx(page.boxes.trim.x);
      // Trim tops of a row align even when bleed/slug differ.
      const mediaY = y + (rowTrimTop - mmToCssPx(page.boxes.trim.y));
      return [
        {
          page,
          frame,
          position: {
            x: mediaX + mmToCssPx(page.boxes.trim.x),
            y: mediaY + mmToCssPx(page.boxes.trim.y),
          },
          mediaPosition: { x: mediaX, y: mediaY },
          masterFrames: (page.masterItems ?? []).map((item) => ({
            item,
            frame: ensure(
              arena.children.find(
                (f) => f.uuid === view.masterFrames[page.id]?.[item.id],
              ),
              `Master item ${page.id}/${item.id} has no publishing editing frame`,
            ),
            position: {
              x: mediaX + mmToCssPx(page.boxes.trim.x + item.position.x),
              y: mediaY + mmToCssPx(page.boxes.trim.y + item.position.y),
            },
          })),
        },
      ];
    });
    lastGap = gapAfter(rowIndex);
    y +=
      Math.max(
        0,
        ...row.map(
          (p) => p.mediaPosition.y - y + mmToCssPx(p.page.boxes.media.height),
        ),
      ) + lastGap;
    return row;
  });
  // Return document order rather than DOM/frame insertion order.
  const ordered = composition.pages.map((page) =>
    ensure(pages.find((p) => p.page.id === page.id)),
  );
  return {
    pages: ordered,
    composition,
    width: Math.max(
      0,
      ...pages.map(
        (p) => p.mediaPosition.x + mmToCssPx(p.page.boxes.media.width),
      ),
    ),
    height: pages.length ? y - lastGap : 0,
  };
}

/** Native tracked mutation, invoked by the caller inside StudioCtx.change.
 * Frames are an editing adapter: composition, not their saved placement, determines layout.
 */
export function reconcilePublishingView(site: Site) {
  let doc = readPublishingDocument(site);
  if (!doc) {
    doc = ensure(
      migrateLegacyBook(site),
      "Initialize a publishing document first",
    );
    writePublishingDocument(site, doc);
  }
  const composition = resolvePublishingComposition(site, doc);
  const mgr = new TplMgr({ site });
  const view = readPublishingView(site) ?? {
    id: mkShortId(),
    frames: {},
    masterFrames: {},
  };
  const matches = site.arenas.filter((a) => a.publishingViewId === view.id);
  if (matches.length > 1)
    throw new Error("Duplicated publishing view identity");
  const arena = matches[0] ?? mgr.addArena("Book spreads");
  arena.publishingViewId = view.id;
  const nextFrames: Record<string, string> = Object.create(null);
  const nextMasterFrames: Record<
    string,
    Record<string, string>
  > = Object.create(null);
  const configure = (
    frame: ArenaFrame,
    page: ResolvedPublishingPage,
    component: Site["components"][number],
    state: ResolvedPublishingPage["printState"],
  ) => {
    frame.width = mmToCssPx(page.geometry.trim.width);
    frame.height = mmToCssPx(page.geometry.trim.height);
    frame.pinnedVariants = Object.fromEntries(
      state.variantIds.map((id) => [id, true]),
    );
    frame.pinnedGlobalVariants = Object.fromEntries(
      state.globalVariantIds.map((id) => [id, true]),
    );
    frame.targetVariants = allComponentVariants(component).filter((v) =>
      state.variantIds.includes(v.uuid),
    );
    frame.targetGlobalVariants = site.globalVariantGroups
      .flatMap((g) => g.variants)
      .filter((v) => state.globalVariantIds.includes(v.uuid));
  };
  const create = (
    component: Site["components"][number],
    page: ResolvedPublishingPage,
  ) =>
    mgr.addNewMixedArenaFrame(arena, component.name, component, {
      width: mmToCssPx(page.geometry.trim.width),
      height: mmToCssPx(page.geometry.trim.height),
      insertPt: new Pt(0, 0),
    });
  for (const page of composition.pages) {
    if (page.component) {
      let frame = arena.children.find((f) => f.uuid === view.frames[page.id]);
      // Occurrence identity belongs to the document. Replacing its content
      // creates a new editing adapter, just like a local master replacement.
      // Previously owned adapters are retired below; native source is retained.
      if (frame?.container.component !== page.component) frame = undefined;
      frame ??= create(page.component, page);
      nextFrames[page.id] = frame.uuid;
      configure(frame, page, page.component, page.printState);
    }
    const masters: Record<string, string> = Object.create(null);
    for (const item of page.masterItems ?? []) {
      let frame = arena.children.find(
        (f) => f.uuid === view.masterFrames[page.id]?.[item.id],
      );
      // An explicit local replacement gets a new adapter; native source trees are untouched.
      if (frame?.container.component !== item.component) frame = undefined;
      frame ??= create(item.component, page);
      masters[item.id] = frame.uuid;
      configure(frame, page, item.component, item.printState);
    }
    if (Object.keys(masters).length) nextMasterFrames[page.id] = masters;
  }
  // Remove only previously owned adapters that are no longer in the document.
  // Foreign user frames are never deleted or adopted.
  const previous = new Set([
    ...Object.values(view.frames),
    ...Object.values(view.masterFrames).flatMap(Object.values),
  ]);
  const retained = new Set([
    ...Object.values(nextFrames),
    ...Object.values(nextMasterFrames).flatMap(Object.values),
  ]);
  arena.children = arena.children.filter(
    (f) => !previous.has(f.uuid) || retained.has(f.uuid),
  );
  site.flags[PUBLISHING_VIEW_KEY] = JSON.stringify(
    publishingViewSchema.parse({
      id: view.id,
      frames: nextFrames,
      masterFrames: nextMasterFrames,
    }),
  );
  const layout = getPublishingViewLayout(site, arena);
  for (const { frame, position, masterFrames } of layout.pages) {
    if (frame) {
      frame.left = position.x;
      frame.top = position.y;
    }
    for (const master of masterFrames) {
      master.frame.left = master.position.x;
      master.frame.top = master.position.y;
    }
  }
  return { arena, layout };
}
